import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calculator, Loader2, X } from 'lucide-react';
import { clearCostEstimate, estimateWalletCost } from '../../../../../store/walletSlice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../tailwind/dialog';
import { Input } from '../../../../tailwind/input';
import { Label } from '../../../../tailwind/label';
import { Button } from '../../../../tailwind/button';
import { cn } from '../../../../../lib/utils';
import {
  buildEstimateCostPayload,
  formatWalletCurrency,
  getTotalMessageCount,
  normalizeEstimateMessageInput,
} from '../../../../../utils/wallet/walletUtils';
import {
  ESTIMATE_COUNTRIES,
  ESTIMATE_CREDIT_COST_LABEL,
  ESTIMATE_MESSAGE_TYPES,
  MAX_ESTIMATE_MESSAGE_COUNT,
  MAX_ESTIMATE_TOTAL_MESSAGES,
} from '../../../../../utils/wallet/walletConstants';
import { waBtn, waFocusRing, waSoft } from '../../../../../pages/whatsapp/utils/whatsappUiTokens';

const DEFAULT_INPUTS = { marketing: 100, utility: 0, auth: 0, service: 0 };
const DEFAULT_COUNTRY = 'IN';

export function CostCalculatorModal({ open, onOpenChange }) {
  const dispatch = useDispatch();
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [validationError, setValidationError] = useState('');
  const [estimateFingerprint, setEstimateFingerprint] = useState(null);

  const costEstimate = useSelector((state) => state.wallet?.costEstimate);
  const costEstimateLoading = useSelector((state) => state.wallet?.costEstimateLoading);
  const costEstimateError = useSelector((state) => state.wallet?.costEstimateError);

  const totalMessages = getTotalMessageCount(inputs);
  const currentFingerprint = JSON.stringify({ country, inputs });
  const hasEstimate =
    costEstimate &&
    estimateFingerprint === currentFingerprint &&
    typeof costEstimate.totalCostWithGst === 'number';
  const rechargeNeeded = hasEstimate && Number(costEstimate.rechargeNeeded) > 0;

  const resetCalculator = () => {
    setInputs({ ...DEFAULT_INPUTS });
    setCountry(DEFAULT_COUNTRY);
    setValidationError('');
    setEstimateFingerprint(null);
    dispatch(clearCostEstimate());
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      resetCalculator();
    }
    onOpenChange(nextOpen);
  };

  const handleInputChange = (typeId, rawValue) => {
    setInputs((prev) => ({
      ...prev,
      [typeId]: normalizeEstimateMessageInput(rawValue),
    }));
    setValidationError('');
    setEstimateFingerprint(null);
    dispatch(clearCostEstimate());
  };

  const handleEstimateCost = () => {
    const result = buildEstimateCostPayload({ country, inputs });
    if (result.error) {
      setValidationError(result.error);
      return;
    }
    setValidationError('');
    setEstimateFingerprint(currentFingerprint);
    dispatch(estimateWalletCost(result.payload));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md [&>button]:hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="relative pr-10">
          <div className="flex items-start gap-3">
            <div className={cn('mt-0.5 h-9 w-9 rounded-full flex items-center justify-center shrink-0', waSoft.bg)}>
              <Calculator className={cn('h-4 w-4', waSoft.text)} />
            </div>
            <div className="min-w-0 text-left">
              <DialogTitle className="text-xl text-gray-900 dark:text-white">
                Credit Cost Calculator
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                Estimate credits for your messaging volume and what you&apos;d pay to purchase them.
              </DialogDescription>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="absolute right-0 top-0 h-8 w-8 rounded-md border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-900 dark:text-white">
              Receiver&apos;s country / region
            </Label>
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setValidationError('');
              }}
              className={cn(
                'flex h-11 w-full items-center rounded-md border border-gray-200 dark:border-slate-800',
                'bg-gray-50 dark:bg-slate-950 px-3 py-2 text-sm text-gray-900 dark:text-white',
                'focus:outline-none',
                waFocusRing
              )}
            >
              {ESTIMATE_COUNTRIES.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-900 dark:text-white">
              Monthly messaging volume
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              0 to {MAX_ESTIMATE_MESSAGE_COUNT.toLocaleString('en-IN')} per type (max {MAX_ESTIMATE_TOTAL_MESSAGES.toLocaleString('en-IN')} total)
            </p>
            <div className="rounded-lg border border-gray-200 dark:border-slate-800 divide-y divide-gray-100 dark:divide-slate-800 overflow-hidden">
              {ESTIMATE_MESSAGE_TYPES.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between gap-4 px-4 py-3 bg-white dark:bg-slate-900"
                >
                  <Label className="text-sm font-normal text-gray-700 dark:text-gray-300">
                    {type.label}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    max={MAX_ESTIMATE_MESSAGE_COUNT}
                    step={1}
                    value={inputs[type.id] === 0 ? '' : inputs[type.id]}
                    onChange={(e) => handleInputChange(type.id, e.target.value)}
                    placeholder="0"
                    className={cn(
                      'w-28 h-10 text-right bg-gray-50 dark:bg-slate-950 border-gray-200 dark:border-slate-800',
                      waFocusRing
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border border-gray-200 dark:border-slate-800 rounded-lg p-5 bg-slate-50 dark:bg-slate-950/60">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Estimate Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">Total messages</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {totalMessages.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">Credits required</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {hasEstimate ? formatWalletCurrency(costEstimate.estimatedCost) : '—'}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-200 dark:border-slate-800 mt-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900 dark:text-white">Amount to pay (incl. GST)</span>
                  <span className={cn('font-bold text-lg', hasEstimate ? waSoft.text : 'text-gray-900 dark:text-white')}>
                    {hasEstimate ? formatWalletCurrency(costEstimate.totalCostWithGst) : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Button
            className={cn(waBtn.primary, 'w-full h-12 text-base gap-2')}
            onClick={handleEstimateCost}
            disabled={costEstimateLoading || totalMessages <= 0}
          >
            {costEstimateLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {costEstimateLoading ? 'Estimating…' : ESTIMATE_CREDIT_COST_LABEL}
          </Button>

          {totalMessages <= 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center -mt-3">
              Enter at least one message volume to estimate credit cost.
            </p>
          )}

          {(validationError || costEstimateError) && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center">
              {validationError || costEstimateError}
            </p>
          )}

          {hasEstimate && (
            <div className="rounded-lg border border-gray-200 dark:border-slate-800 overflow-hidden text-sm divide-y divide-gray-100 dark:divide-slate-800">
              <div className="flex justify-between px-4 py-3 bg-white dark:bg-slate-900">
                <span className="text-gray-600 dark:text-gray-400">Available credits</span>
                <span
                  className={cn(
                    'font-medium',
                    rechargeNeeded ? 'text-red-600 dark:text-red-400' : waSoft.text
                  )}
                >
                  {formatWalletCurrency(costEstimate.currentBalance)}
                </span>
              </div>
              {rechargeNeeded ? (
                <>
                  <div className="flex justify-between px-4 py-3 bg-white dark:bg-slate-900">
                    <span className="text-gray-600 dark:text-gray-400">Credits to purchase</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatWalletCurrency(costEstimate.rechargeNeeded, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                  <div className={cn('flex justify-between px-4 py-3', waSoft.bg)}>
                    <span className="font-semibold text-gray-900 dark:text-white">Recharge needed (incl. GST)</span>
                    <span className={cn('font-bold', waSoft.text)}>
                      {formatWalletCurrency(costEstimate.rechargeCostWithGst)}
                    </span>
                  </div>
                </>
              ) : (
                <div className={cn('px-4 py-3', waSoft.bg, waSoft.text)}>
                  You have enough credits for this estimate.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
